/**
 * API Client
 *
 * Fetch-based API client for communicating with the FastAPI backend.
 * Uses HttpOnly cookie auth with automatic token refresh on 401.
 */


// =============================================================================
// API URL Resolution
// =============================================================================

// Cached API URL — resolved once at startup via initApiUrl()
let _resolvedApiUrl: string | null = null;

/**
 * Return the resolved API base URL synchronously.
 * Falls back to VITE_API_URL env var, then '/api/v1'.
 */
export function getApiBaseUrl(): string {
  return _resolvedApiUrl ?? import.meta.env.VITE_API_URL ?? '/api/v1';
}

/**
 * Resolve the API base URL at startup.
 *
 * In Electron, reads the persisted URL from userData settings.
 * In the browser, falls back to the VITE_API_URL env variable or '/api/v1'.
 * Call this once before the first API request (e.g. in main.tsx).
 */
export async function initApiUrl(): Promise<void> {
  if (typeof window !== 'undefined' && window.electron?.getApiUrl) {
    const electronUrl = await window.electron.getApiUrl();
    if (electronUrl) {
      _resolvedApiUrl = electronUrl;
      return;
    }
  }
  _resolvedApiUrl = import.meta.env.VITE_API_URL ?? '/api/v1';
}

// =============================================================================
// Error Handling
// =============================================================================

/**
 * API Error class for RFC 7807 problem details
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public title: string,
    public detail?: string
  ) {
    super(detail || title);
    this.name = 'ApiError';
  }
}

// =============================================================================
// Token Refresh Handling
// =============================================================================

// Callback for token refresh - set by auth module
let tokenRefreshCallback: (() => Promise<boolean>) | null = null;

// Callback invoked when 401 refresh fails - lets auth context reset React state
let authFailureCallback: (() => void) | null = null;

// Mutex: store the in-flight refresh promise so concurrent 401s share one refresh
let activeRefreshPromise: Promise<boolean> | null = null;

/**
 * Set the callback used to refresh auth tokens.
 * Called by the auth module during initialization.
 */
export function setTokenRefreshCallback(callback: () => Promise<boolean>) {
  tokenRefreshCallback = callback;
}

/**
 * Set the callback invoked when a 401 occurs and token refresh fails.
 * This allows the auth context to transition state to 'unauthenticated'.
 */
export function setAuthFailureCallback(callback: () => void) {
  authFailureCallback = callback;
}

// =============================================================================
// CSRF Token Handling
// =============================================================================

const CSRF_COOKIE_NAME = 'csrf_token';
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Read CSRF token from cookie (set by the backend as non-HttpOnly).
 */
export function getCsrfToken(): string | undefined {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match?.split('=')[1];
}

// =============================================================================
// Core Fetch Wrapper
// =============================================================================

/**
 * Query parameters type for API requests.
 * Allows any object with string/number/boolean values.
 */
export type QueryParams = Record<string, string | number | boolean | undefined | null>;

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, unknown>;
  skipAuth?: boolean; // Skip auth cookie injection (for public endpoints)
}

/**
 * Generic fetch wrapper with automatic cookie-based auth and error handling.
 * Uses credentials: 'include' to send HttpOnly cookies with every request.
 */
export async function fetchApi<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const response = await fetchWithAuth<T>(endpoint, options);
  return response;
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: FetchOptions,
  isRetry = false
): Promise<T> {
  const isFormData = options.body instanceof FormData;

  const headers: HeadersInit = {
    // Omit Content-Type for FormData — the browser sets it with the multipart boundary
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  };

  // Attach CSRF token on mutation requests
  const method = (options.method || 'GET').toUpperCase();
  if (MUTATION_METHODS.has(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      (headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
    }
  }

  // Build URL with query params
  let url = `${getApiBaseUrl()}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Send HttpOnly cookies automatically
    body: isFormData ? options.body as FormData : (options.body ? JSON.stringify(options.body) : undefined),
  });

  // Handle 401 Unauthorized - attempt token refresh (with mutex to prevent races)
  if (response.status === 401 && !isRetry && !options.skipAuth && tokenRefreshCallback) {
    // If a refresh is already in flight, await that instead of starting a new one
    if (!activeRefreshPromise) {
      activeRefreshPromise = tokenRefreshCallback().finally(() => {
        activeRefreshPromise = null;
      });
    }
    const refreshed = await activeRefreshPromise;
    if (refreshed) {
      // Retry the request with the new cookie
      return fetchWithAuth<T>(endpoint, options, true);
    }
    // Refresh failed — notify auth context to redirect to login
    if (authFailureCallback) {
      authFailureCallback();
    }
  }

  if (!response.ok) {
    // Parse RFC 7807 error response
    try {
      const error = await response.json();
      throw new ApiError(
        error.status || response.status,
        error.title || response.statusText,
        error.detail
      );
    } catch (e) {
      if (e instanceof ApiError) throw e;
      throw new ApiError(response.status, response.statusText);
    }
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}

// =============================================================================
// API Endpoints
// =============================================================================

export const api = {};
